import React, { useMemo } from "react";

import { Delete as DeleteIcon } from "@material-ui/icons";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";

import MediaAPI from "../../api/MediaAPI";
import { CastingsTypes } from "../../utils/data/Castings";
import {
  AVAILABLE_METADATA_FILTERS,
  getMetadatas,
} from "../../utils/data/metadata";
import {
  PRODUCTION_YEAR_FUTURE_OFFSET,
  PRODUCTION_YEAR_START,
} from "../contentTabs/TabContentMetadata";
import { getSelectClassNames } from "../form/Select";

import type { Dispatch, JSX, SetStateAction } from "react";
import type { SingleValue } from "react-select";

import type {
  GetSmartlistResponse,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";

type FilterType = "metadata" | "castings";

interface FilterOption {
  value: string;
  label: string;
}

interface CriteriaOption extends FilterOption {
  filterType: FilterType;
}

// Le type API `filter` (Record<string, string[]>) ne modélise pas la clé
// imbriquée `castings`, qui contient elle-même un Record. On décrit ici la
// forme réelle des données manipulées par le composant.
type CastingsFilter = Record<string, string[]>;

interface SmartlistFilter {
  castings?: CastingsFilter;
  [key: string]: string[] | CastingsFilter | undefined;
}

interface SmartlistCriteriaFilterProps {
  smartlist: GetSmartlistResponse | UpdatedSmartlist;
  onChangeFilter: Dispatch<SetStateAction<UpdatedSmartlist>>;
  loading: boolean;
}

// Stable across renders — no inputs vary
const selectClassNames = getSelectClassNames();

const getCastingListQuery = MediaAPI.generateQuery({
  fn: MediaAPI.getCastingList,
  params: [{ perPage: 100000 }],
});

function SmartlistCriteriaFilter({
  smartlist,
  onChangeFilter,
  loading,
}: SmartlistCriteriaFilterProps): JSX.Element {
  const { t } = useTranslation("editor-smartlist");

  const { data: castingResponse } = useQuery(getCastingListQuery);
  const castingItemList = castingResponse?.data;

  const metadatas = useSelector(
    (state: AppState) => state.fetchReducer.metadatas,
  );
  const currentLanguage = useSelector(
    (state: AppState) => state.languages.current,
  );

  const languageId = currentLanguage?.uuid;

  const filter = (smartlist.filter ?? {}) as SmartlistFilter;

  const getFilterArray = (key: string, isCastings: boolean): string[] =>
    (isCastings
      ? filter.castings?.[key]
      : (filter[key] as string[] | undefined)) ?? [];

  const onChange = ({
    key,
    index,
    value,
    isCastings,
  }: {
    key: string;
    index: number;
    value: string;
    isCastings: boolean;
  }) => {
    const arrayChanged = getFilterArray(key, isCastings).map((item, i) =>
      i === index ? value : item,
    );

    const newSmartlist = { ...smartlist };
    const newFilter = newSmartlist.filter as SmartlistFilter;

    if (isCastings) {
      newFilter.castings = { ...newFilter.castings, [key]: arrayChanged };
    } else {
      newFilter[key] = arrayChanged;
    }

    onChangeFilter(newSmartlist as UpdatedSmartlist);
  };

  const removeLine = ({
    key,
    index,
    isCastings,
  }: {
    key: string;
    index: number;
    isCastings: boolean;
  }) => {
    const arrayChanged = getFilterArray(key, isCastings).filter(
      (_, i) => i !== index,
    );
    const isEmpty = arrayChanged.length === 0;
    const newSmartlist = { ...smartlist };
    const newFilter = newSmartlist.filter as SmartlistFilter;

    if (isCastings) {
      const castings = newFilter.castings ?? {};
      if (isEmpty) {
        delete castings[key];
        if (Object.keys(castings).length === 0) {
          delete newFilter.castings;
        }
      } else {
        castings[key] = arrayChanged;
      }
    } else if (isEmpty) {
      delete newFilter[key];
    } else {
      newFilter[key] = arrayChanged;
    }

    onChangeFilter(newSmartlist as UpdatedSmartlist);
  };

  const addLine = (key: string, filterType: FilterType) => {
    const isCasting = filterType === "castings";
    const arrayFilter = getFilterArray(key, isCasting);
    const newSmartlist = { ...smartlist };
    const newFilter = newSmartlist.filter as SmartlistFilter;

    if (isCasting) {
      newFilter.castings = {
        ...newFilter.castings,
        [key]: [...arrayFilter, ""],
      };
    } else {
      newFilter[key] = [...arrayFilter, ""];
    }

    onChangeFilter(newSmartlist as UpdatedSmartlist);
  };

  const selectOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const yearList: FilterOption[] = [];
    const currentYear = moment().year() + PRODUCTION_YEAR_FUTURE_OFFSET;

    for (let year = currentYear; year >= PRODUCTION_YEAR_START; year -= 1) {
      yearList.push({ value: year.toString(), label: year.toString() });
    }

    const metadataSelect = (AVAILABLE_METADATA_FILTERS as string[]).reduce<
      Record<string, FilterOption[]>
    >((acc, current) => {
      acc[current] = getMetadatas({
        metadatas,
        type: current,
        languageId,
        withTranslations: !["keywords", "duration_types"].includes(current),
      });
      return acc;
    }, {});

    const castingOptionList: FilterOption[] = (castingItemList ?? []).map(
      (casting) => ({ value: casting.uuid, label: casting.name }),
    );

    const castingsSelect = Object.fromEntries(
      (CastingsTypes as string[]).map((key) => [key, castingOptionList]),
    );

    return { ...metadataSelect, ...castingsSelect, production_date: yearList };
  }, [metadatas, castingItemList, languageId]);

  const filterList = useMemo<CriteriaOption[]>(
    () => [
      ...(AVAILABLE_METADATA_FILTERS as string[]).map((key) => ({
        value: key,
        label: t(key, { ns: "editor-advancedsearch" }),
        filterType: "metadata" as FilterType,
      })),
      ...(CastingsTypes as string[]).map((key) => ({
        value: key,
        label: t(key, { ns: "editor-advancedsearch" }),
        filterType: "castings" as FilterType,
      })),
    ],
    [t],
  );

  const renderSmartlistFilters = Object.keys(filter).map((key, indexKey) => {
    const isCastings = key === "castings";

    const renderLine = (keyLine: string, value: string, index: number) => {
      const defaultValue = selectOptions[keyLine]?.find(
        (item) => item.value === value,
      );
      return (
        <div
          key={`${keyLine}-${defaultValue?.value ?? index}-${languageId}`}
          className="smartlist-conditional-filter"
        >
          <span className="span-and">
            {indexKey === 0 && index === 0 ? "" : t("and")}
          </span>
          <span className="span-filter border-input flex items-center justify-center border border-solid">
            {t(keyLine, { ns: "editor-advancedsearch" })}
          </span>
          <span className="span-equal">{t("isEqual")}</span>
          <div className="editor-select-smartlist-filter">
            <Select<FilterOption>
              classNamePrefix="slct"
              classNames={selectClassNames}
              defaultValue={defaultValue}
              isLoading={loading}
              options={selectOptions[keyLine]}
              placeholder={t("select")}
              onChange={(option) =>
                onChange({
                  key: keyLine,
                  index,
                  value: option?.value ?? "",
                  isCastings,
                })
              }
            />
          </div>
          <DeleteIcon
            className="btn-remove"
            onClick={() => removeLine({ key: keyLine, index, isCastings })}
          />
        </div>
      );
    };

    if (isCastings) {
      const castings = filter.castings ?? {};
      return Object.keys(castings).map((castingKey) =>
        (castings[castingKey] ?? []).map((value, index) =>
          renderLine(castingKey, value, index),
        ),
      );
    }

    const valueList = (filter[key] as string[] | undefined) ?? [];
    return valueList.map((value, index) => renderLine(key, value, index));
  });

  const handleChangeCriteria = (option: SingleValue<CriteriaOption>) => {
    if (option) {
      addLine(option.value, option.filterType);
    }
  };

  return (
    <div className="editor-container-smartlist-conditional">
      <Select<CriteriaOption>
        closeMenuOnSelect
        className="editor-select-smartlist-conditional"
        classNamePrefix="slct"
        classNames={selectClassNames}
        name="form-field-name"
        onChange={handleChangeCriteria}
        options={filterList}
        placeholder={t("criteriaPlaceholder")}
      />
      {renderSmartlistFilters}
      <div className="result-items">
        <span>{t("resultNumber")}</span>
        <span className="result-number">
          ({smartlist.smartlist_items?.length || 0})
        </span>
      </div>
    </div>
  );
}

export default SmartlistCriteriaFilter;
